/**
 * Groups section (PRD §1 "Groups section", §5.1 #11-#15; redesign § Groups table).
 *
 * Section head (title + "New Group") sits outside the island; the island is a kit table with
 * client-side sorting. Columns: name with the member count appended, last scrape, next scrape,
 * mode as plain text, and the server-derived aggregate status (dot + grey text). Every cell is a
 * real link to the group view (so middle-click and Ctrl-click open a tab); every row action —
 * view, rename, manage members, scrape members now, scraping mode, delete — lives in the kebab. Deleting is confirmed and says out loud that member
 * profiles are untouched.
 */

import { useMemo, useState, type ReactNode } from 'react';
import { Link, useNavigate } from 'react-router';
import {
  SCHEDULE_MODES,
  type GroupDTO,
  type GroupStatus,
  type ProfileRowDTO,
} from '@lw/shared';
import { viewPath } from '../../app/routes';
import { describeError } from '../../api/client';
import {
  ConfirmDialog,
  EmptyState,
  ErrorState,
  KebabIcon,
  Menu,
  MenuItem,
  MenuSeparator,
  PlusIcon,
  Spinner,
} from '../../components';
import { formatDateTime, formatRelativeTime } from '../../lib/datetime';
import { pluralize } from '../../lib/format';
import { SCHEDULE_MODE_LABEL } from '../../lib/labels';
import {
  CreateGroupDialog,
  ManageMembersDialog,
  RenameGroupDialog,
  WatchingGroupDialog,
} from './GroupDialogs';
import { NotificationsMenuItems } from './NotificationsMenuItems';
import { ScheduleModeMenuItems } from './ScheduleModeMenuItems';
import { SortableHeader, type SortState } from './SortableHeader';
import { WatchToggle } from './WatchToggle';
import {
  useDeleteGroup,
  useSetNotifications,
  useSetWatching,
  useUpdateGroup,
} from './useHomeData';

const GROUP_STATUS_LABEL: Record<GroupStatus, string> = {
  ok: 'OK',
  queued: 'Queued',
  running: 'Running',
  error: 'Error',
};

/** Worst first when sorting by status. */
const GROUP_STATUS_RANK: Record<GroupStatus, number> = {
  error: 0,
  running: 1,
  queued: 2,
  ok: 3,
};

type GroupSortKey =
  | 'name'
  | 'lastScrapedAt'
  | 'nextScrapeAt'
  | 'scheduleMode'
  | 'watching'
  | 'status';
type GroupSort = SortState<GroupSortKey>;

function sortGroups(rows: GroupDTO[], sort: GroupSort): GroupDTO[] {
  const factor = sort.dir === 'asc' ? 1 : -1;

  const numeric = (group: GroupDTO): number | null => {
    switch (sort.key) {
      case 'lastScrapedAt':
        return group.lastScrapedAt;
      case 'nextScrapeAt':
        return group.nextScrapeAt;
      case 'scheduleMode':
        return SCHEDULE_MODES.indexOf(group.scheduleMode);
      case 'watching':
        return group.watching.effectiveOn ? 1 : 0;
      case 'status':
        return GROUP_STATUS_RANK[group.status];
      default:
        return null;
    }
  };

  return [...rows].sort((a, b) => {
    let result: number;
    if (sort.key === 'name') {
      result = a.name.localeCompare(b.name);
    } else {
      const left = numeric(a);
      const right = numeric(b);
      // "—" cells sink to the bottom in both directions.
      if (left === null || right === null) {
        if (left !== right) return left === null ? 1 : -1;
        result = 0;
      } else {
        result = left - right;
      }
    }
    if (result !== 0) return result * factor;
    return a.name.localeCompare(b.name) || a.id - b.id;
  });
}

type GroupDialog =
  | { kind: 'rename'; group: GroupDTO }
  | { kind: 'members'; group: GroupDTO }
  | { kind: 'watching'; group: GroupDTO }
  | { kind: 'delete'; group: GroupDTO };

export type GroupsSectionProps = {
  groups: GroupDTO[];
  profiles: ProfileRowDTO[];
  timezone: string;
  isPending: boolean;
  error: unknown;
  onRetry: () => void;
  onScrapeGroup: (groupId: number) => void;
  /** Create dialog is controlled by the page, so the profiles table's "New group…" can open it. */
  createOpen: boolean;
  onOpenCreate: () => void;
  onCloseCreate: () => void;
};

export function GroupsSection({
  groups,
  profiles,
  timezone,
  isPending,
  error,
  onRetry,
  onScrapeGroup,
  createOpen,
  onOpenCreate,
  onCloseCreate,
}: GroupsSectionProps) {
  const navigate = useNavigate();
  const [sort, setSort] = useState<GroupSort>({ key: 'name', dir: 'asc' });
  const [dialog, setDialog] = useState<GroupDialog | null>(null);
  const updateGroup = useUpdateGroup();
  const removeGroup = useDeleteGroup();
  const setWatching = useSetWatching();
  const setNotifications = useSetNotifications();

  const rows = useMemo(() => sortGroups(groups, sort), [groups, sort]);

  // Which row #70 is writing, if any — only that switch waits. The row toggle has no error surface
  // of its own, so the saving bar below the table carries both states, exactly as it does for the
  // profiles table's cell editors.
  const watchWrite = setWatching.isPending ? setWatching.variables : undefined;
  const groupSaving = setWatching.isPending || setNotifications.isPending;
  const groupMutationError = setWatching.error ?? setNotifications.error;

  const onSort = (key: GroupSortKey) => {
    setSort((current) =>
      current.key === key
        ? { key, dir: current.dir === 'asc' ? 'desc' : 'asc' }
        : { key, dir: key === 'name' ? 'asc' : 'desc' },
    );
  };

  const closeDialog = () => setDialog(null);

  return (
    <section className="section">
      <div className="section-head">
        <h2 className="section-title">Groups</h2>
        <div className="section-head__actions">
          <button type="button" className="btn-ghost" onClick={onOpenCreate}>
            <PlusIcon />
            New Group
          </button>
        </div>
      </div>

      <div className="card card--table">
        {isPending ? <Spinner size="md" label="Loading groups…" /> : null}

        {error !== undefined && error !== null ? (
          <ErrorState title="Couldn't load groups" error={error} onRetry={onRetry} />
        ) : null}

        {!isPending && (error === undefined || error === null) && groups.length === 0 ? (
          <EmptyState
            title="No groups yet"
            description="Group profiles to see their feed, posts and metrics side by side. A profile can belong to any number of groups."
            action={
              <button type="button" className="btn-primary" onClick={onOpenCreate}>
                <PlusIcon />
                New group
              </button>
            }
          />
        ) : null}

        {rows.length > 0 ? (
          <div className="table-wrap">
            <table className="table table--fit">
              <thead>
                <tr>
                  <SortableHeader label="Name" columnKey="name" sort={sort} onSort={onSort} />
                  <SortableHeader
                    label="Last scrape"
                    columnKey="lastScrapedAt"
                    sort={sort}
                    onSort={onSort}
                  />
                  <SortableHeader
                    label="Next scrape"
                    columnKey="nextScrapeAt"
                    sort={sort}
                    onSort={onSort}
                  />
                  <SortableHeader
                    label="Mode"
                    columnKey="scheduleMode"
                    sort={sort}
                    onSort={onSort}
                  />
                  <SortableHeader
                    label="Watch"
                    columnKey="watching"
                    sort={sort}
                    onSort={onSort}
                  />
                  <SortableHeader label="Status" columnKey="status" sort={sort} onSort={onSort} />
                  <th aria-label="Actions" />
                </tr>
              </thead>
              <tbody>
                {rows.map((group) => {
                  const to = viewPath('group', group.id);
                  const goToView = () => void navigate(to);
                  // The name cell holds no anchor of its own, so it is one cell-filling link and
                  // the row's single labelled tab stop; the rest repeat it silently.
                  const cellLink = (children: ReactNode) => (
                    <Link className="row-link" to={to} tabIndex={-1} aria-hidden="true">
                      {children}
                    </Link>
                  );
                  return (
                    <tr key={group.id}>
                      <td className="cell-linked">
                        <Link className="row-link" to={to}>
                          <div className="cell-name__line">
                            <span className="cell-name" title={group.name}>
                              {group.name}
                            </span>
                            <span className="cell-name__count">• {group.memberCount}</span>
                          </div>
                        </Link>
                      </td>
                      <td
                        className="cell-muted cell-linked"
                        title={formatDateTime(group.lastScrapedAt, timezone)}
                      >
                        {cellLink(
                          group.lastScrapedAt === null
                            ? '—'
                            : formatRelativeTime(group.lastScrapedAt),
                        )}
                      </td>
                      <td
                        className="cell-muted cell-linked"
                        title={formatDateTime(group.nextScrapeAt, timezone)}
                      >
                        {cellLink(
                          group.nextScrapeAt === null
                            ? '—'
                            : formatRelativeTime(group.nextScrapeAt),
                        )}
                      </td>
                      <td className="cell-muted cell-linked">
                        {cellLink(SCHEDULE_MODE_LABEL[group.scheduleMode])}
                      </td>
                      <td className="cell-watch">
                        <WatchToggle
                          watching={group.watching}
                          subjectLabel={group.name}
                          pending={
                            watchWrite !== undefined &&
                            watchWrite.t === 'group' &&
                            watchWrite.id === group.id
                          }
                          onToggle={(enabled) =>
                            setWatching.mutate({ t: 'group', id: group.id, body: { enabled } })
                          }
                        />
                      </td>
                      <td className="cell-linked">
                        {cellLink(
                          <span className={`status status--${group.status}`}>
                            <span className="status__dot" aria-hidden="true" />
                            {GROUP_STATUS_LABEL[group.status]}
                          </span>,
                        )}
                      </td>
                      <td className="cell-menu">
                        <Menu
                          label={<KebabIcon />}
                          ariaLabel={`Actions for ${group.name}`}
                          align="right"
                          triggerClassName="icon-btn row-menu"
                        >
                          {(close) => (
                            <>
                              <MenuItem
                                onClick={() => {
                                  close();
                                  goToView();
                                }}
                              >
                                View
                              </MenuItem>
                              <MenuItem
                                onClick={() => {
                                  close();
                                  setDialog({ kind: 'rename', group });
                                }}
                              >
                                Rename
                              </MenuItem>
                              <MenuItem
                                onClick={() => {
                                  close();
                                  setDialog({ kind: 'members', group });
                                }}
                              >
                                Manage members
                              </MenuItem>
                              <MenuItem
                                disabled={group.memberCount === 0}
                                onClick={() => {
                                  close();
                                  onScrapeGroup(group.id);
                                }}
                              >
                                Scrape members now
                              </MenuItem>
                              <MenuSeparator />
                              <ScheduleModeMenuItems
                                current={group.scheduleMode}
                                disabled={updateGroup.isPending}
                                onSelect={(scheduleMode) => {
                                  close();
                                  updateGroup.mutate({ id: group.id, body: { scheduleMode } });
                                }}
                              />
                              <MenuItem
                                onClick={() => {
                                  close();
                                  setDialog({ kind: 'watching', group });
                                }}
                              >
                                Watching…
                              </MenuItem>
                              <MenuSeparator />
                              {/* Checkboxes, so the menu stays open across both toggles. */}
                              <NotificationsMenuItems
                                current={group.notifications}
                                disabled={setNotifications.isPending}
                                onToggle={(patch) =>
                                  setNotifications.mutate({
                                    t: 'group',
                                    id: group.id,
                                    body: patch,
                                  })
                                }
                              />
                              <MenuSeparator />
                              <MenuItem
                                destructive
                                onClick={() => {
                                  close();
                                  setDialog({ kind: 'delete', group });
                                }}
                              >
                                Delete group
                              </MenuItem>
                            </>
                          )}
                        </Menu>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        ) : null}

        {groupSaving ? (
          <div className="table-saving" role="status">
            <Spinner size="sm" />
            Saving…
          </div>
        ) : null}

        {!groupSaving && groupMutationError !== null ? (
          <div className="table-saving table-saving--error" role="alert">
            {describeError(groupMutationError)}
          </div>
        ) : null}
      </div>

      {createOpen ? <CreateGroupDialog profiles={profiles} onClose={onCloseCreate} /> : null}

      {dialog?.kind === 'rename' ? (
        <RenameGroupDialog group={dialog.group} onClose={closeDialog} />
      ) : null}

      {dialog?.kind === 'members' ? (
        <ManageMembersDialog group={dialog.group} profiles={profiles} onClose={closeDialog} />
      ) : null}

      {dialog?.kind === 'watching' ? (
        <WatchingGroupDialog group={dialog.group} onClose={closeDialog} />
      ) : null}

      {dialog?.kind === 'delete' ? (
        <ConfirmDialog
          open
          destructive
          title={`Delete “${dialog.group.name}”?`}
          confirmLabel="Delete group"
          busy={removeGroup.isPending}
          message={
            <p>
              This deletes the group only — its {pluralize(dialog.group.memberCount, 'member')} and
              all their data are untouched.
            </p>
          }
          onCancel={closeDialog}
          onConfirm={() => removeGroup.mutate(dialog.group.id, { onSuccess: closeDialog })}
        />
      ) : null}
    </section>
  );
}

export default GroupsSection;
